import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { JobCard, JobCardSkeleton } from "../components/JobCard";
import { EmptyState } from "../components/EmptyState";
import { DistanceFilter, GpsNearButton } from "../components/DistanceFilter";
import { SearchBox } from "../components/SearchBox";
import { QuickFilterChips } from "../components/QuickFilterChips";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchX, SlidersHorizontal, LayoutGrid, List } from "lucide-react";
import { SaveSearchButton } from "../components/SaveSearchButton";
import { ResultsMap } from "../components/ResultsMap";
import { ActiveFilterTags } from "../components/ActiveFilterTags";

const ALL = "__all__";

function FilterSelect({ label, value, onChange, options, testId, counts }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">{label}</p>
      <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
        <SelectTrigger data-testid={testId} className="bg-white"><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value={ALL}>Todos</SelectItem>
          {options.map((o) => {
            const c = counts ? counts[o] : undefined;
            return (
              <SelectItem key={o} value={o}>
                {o}{c != null ? ` (${c})` : ""}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}

function toCountMap(facetArr) {
  const m = {};
  (facetArr || []).forEach((f) => { m[f.value] = f.count; });
  return m;
}

export default function Jobs() {
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [facets, setFacets] = useState({});
  const [loadingMore, setLoadingMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const PAGE_SIZE = 24;
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("f2j_jobs_view") || "grid"; } catch { return "grid"; }
  });
  const get = (k) => params.get(k) || "";

  useEffect(() => {
    try { localStorage.setItem("f2j_jobs_view", view); } catch { /* ignore */ }
  }, [view]);

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const baseParams = useCallback(() => {
    const p = {};
    ["q", "district", "concelho", "category", "regime", "work_mode", "days",
     "featured", "has_cover",
     "near_district", "near_concelho", "radius_km", "sort"].forEach((k) => {
      if (params.get(k)) p[k] = params.get(k);
    });
    return p;
  }, [params]);

  const fetchJobs = useCallback(() => {
    const p = { ...baseParams(), limit: PAGE_SIZE, offset: 0 };
    api.get("/jobs", { params: p }).then((r) => {
      setJobs(r.data.jobs);
      setTotal(r.data.total || 0);
      setHasMore(!!r.data.has_more);
      setFacets(r.data.facets || {});
    }).catch(() => { setJobs([]); setTotal(0); setHasMore(false); setFacets({}); });
  }, [baseParams]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const loadMore = async () => {
    if (loadingMore || !jobs) return;
    setLoadingMore(true);
    try {
      const p = { ...baseParams(), limit: PAGE_SIZE, offset: jobs.length };
      const r = await api.get("/jobs", { params: p });
      setJobs((prev) => [...(prev || []), ...(r.data.jobs || [])]);
      setHasMore(!!r.data.has_more);
      setTotal(r.data.total || 0);
    } catch { /* ignore */ }
    finally { setLoadingMore(false); }
  };

  // Infinite scroll — dispara loadMore quando o sentinela aparece no viewport
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!hasMore || !sentinelRef.current) return;
    const el = sentinelRef.current;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loadingMore) loadMore();
    }, { rootMargin: "300px" });
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, jobs, loadingMore]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    if (k === "district") {
      next.delete("concelho");
      // Distrito escolhido explicitamente substitui o filtro de distância
      ["near_district", "near_concelho", "radius_km"].forEach((x) => next.delete(x));
    }
    setParams(next);
  };

  const setFilters = (obj) => {
    const next = new URLSearchParams(params);
    Object.entries(obj).forEach(([k, v]) => {
      if (v) next.set(k, v); else next.delete(k);
    });
    setParams(next);
  };

  const concelhos = meta && get("district") ? meta.districts[get("district")] || [] : [];

  // Nº de filtros ativos — mostrado no botão "Filtros" em mobile.
  const FILTER_BADGE_KEYS = ["q", "district", "concelho", "category", "regime",
    "work_mode", "days", "featured", "has_cover", "near_concelho"];
  const activeFilterCount = FILTER_BADGE_KEYS.filter((k) => params.get(k)).length;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
        <h1 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl text-slate-900 mb-8">Procurar empregos</h1>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-2 mb-6">
          <SearchBox
            variant="page"
            className="flex-1"
            initialQ={get("q")}
            initialWhere={get("concelho") || get("district")}
          />
          <GpsNearButton setFilters={setFilters} radius={get("radius_km")} className="lg:hidden h-12" />
          <Button data-testid="toggle-filters-btn" variant="outline" className="rounded-full lg:hidden h-12" onClick={() => setShowFilters(!showFilters)}>
            <SlidersHorizontal className="w-4 h-4 mr-2" /> Filtros
            {activeFilterCount > 0 && (
              <span data-testid="active-filter-count" className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-slate-900 text-white text-xs font-bold">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <aside className={`${showFilters ? "block" : "hidden"} lg:block space-y-5`}>
            {meta && (
              <>
                <DistanceFilter meta={meta} get={get} setFilters={setFilters} districtCounts={toCountMap(facets.district)} />
                <FilterSelect label="Distrito" testId="filter-district" value={get("district")} onChange={(v) => setFilter("district", v)} options={Object.keys(meta.districts)} counts={toCountMap(facets.district)} />
                {concelhos.length > 0 && (
                  <FilterSelect label="Concelho" testId="filter-concelho" value={get("concelho")} onChange={(v) => setFilter("concelho", v)} options={concelhos} counts={toCountMap(facets.concelho)} />
                )}
                <FilterSelect label="Categoria" testId="filter-category" value={get("category")} onChange={(v) => setFilter("category", v)} options={meta.categories} counts={toCountMap(facets.category)} />
                <FilterSelect label="Regime" testId="filter-regime" value={get("regime")} onChange={(v) => setFilter("regime", v)} options={meta.regimes} counts={toCountMap(facets.regime)} />
                <FilterSelect label="Modalidade" testId="filter-workmode" value={get("work_mode")} onChange={(v) => setFilter("work_mode", v)} options={meta.work_modes} counts={toCountMap(facets.work_mode)} />
                <FilterSelect label="Publicado" testId="filter-days" value={get("days")} onChange={(v) => setFilter("days", v)} options={["1", "3", "7", "30"]} />
              </>
            )}
          </aside>
          <main className="lg:col-span-3">
            {jobs === null ? (
              <div className="space-y-4">
                <div className="h-4 w-40 bg-slate-200 rounded animate-pulse" />
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <JobCardSkeleton key={i} variant="grid" />
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <ResultsMap filters={baseParams()}
                  onSelectConcelho={get("near_concelho") ? undefined : (pt) => setFilters({ district: pt.district, concelho: pt.concelho, near_district: "" })} />
                <QuickFilterChips params={params} onToggle={setFilter} onClear={(keys) => setFilters(Object.fromEntries(keys.map((k) => [k, ""])))} facets={facets} categories={meta?.categories || []} />
                <ActiveFilterTags params={params} onRemove={(keys) => setFilters(Object.fromEntries(keys.map((k) => [k, ""])))} onClearAll={() => setParams({})} />
                {jobs.length === 0 ? (
                  <EmptyState icon={SearchX} title="Sem resultados" description="Nenhuma vaga corresponde aos filtros. Remove um filtro acima ou limpa tudo."
                    actionLabel="Limpar filtros" onAction={() => setParams({})} />
                ) : (
                <>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p data-testid="jobs-count" className="text-sm text-slate-500">{total} vaga{total !== 1 ? "s" : ""} encontrada{total !== 1 ? "s" : ""}{jobs.length < total ? ` · a mostrar ${jobs.length}` : ""}</p>
                    {[...params.keys()].length > 0 && (
                      <button
                        type="button"
                        data-testid="clear-all-btn"
                        onClick={() => setParams({})}
                        className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-slate-500 hover:text-slate-900 border border-slate-200 bg-white"
                      >
                        <SearchX className="w-3.5 h-3.5" /> Limpar tudo
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <SaveSearchButton />
                    <Select value={get("sort") || "recent"} onValueChange={(v) => setFilter("sort", v === "recent" ? "" : v)}>
                      <SelectTrigger data-testid="sort-select" className="bg-white h-9 rounded-full text-xs font-semibold w-[190px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="recent">Mais recentes</SelectItem>
                        <SelectItem value="salary">Melhor pagas</SelectItem>
                        <SelectItem value="distance" disabled={!get("near_concelho")}>
                          {get("near_concelho") ? "Mais perto de mim" : "Mais perto (define concelho)"}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="inline-flex rounded-full border border-slate-200 bg-white p-1 shadow-sm" role="group" aria-label="Modo de visualização">
                      <button
                        type="button"
                        data-testid="view-list-btn"
                        aria-pressed={view === "list"}
                        onClick={() => setView("list")}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${view === "list" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
                      >
                        <List className="w-3.5 h-3.5" /> Lista
                      </button>
                      <button
                        type="button"
                        data-testid="view-grid-btn"
                        aria-pressed={view === "grid"}
                        onClick={() => setView("grid")}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${view === "grid" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
                      >
                        <LayoutGrid className="w-3.5 h-3.5" /> Grid
                      </button>
                    </div>
                  </div>
                </div>
                <div
                  data-testid="jobs-container"
                  data-view={view}
                  className={view === "grid" ? "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" : "space-y-4"}
                >
                  {jobs.map((j) => <JobCard key={j.id} job={j} compact={view === "grid"} />)}
                </div>
                {hasMore && (
                  <>
                    {loadingMore && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                        {Array.from({ length: 3 }).map((_, i) => (
                          <JobCardSkeleton key={`sk-${i}`} variant={view === "grid" ? "grid" : "list"} />
                        ))}
                      </div>
                    )}
                    <div ref={sentinelRef} aria-hidden="true" className="h-4" />
                    <div className="flex justify-center pt-2">
                      <Button
                        data-testid="load-more-btn"
                        variant="outline"
                        className="rounded-full h-11 px-8"
                        onClick={loadMore}
                        disabled={loadingMore}
                      >
                        {loadingMore ? "A carregar…" : `Ver mais vagas (${total - jobs.length} restantes)`}
                      </Button>
                    </div>
                  </>
                )}
                </>
                )}
              </div>
            )}
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
}
